import { Injectable } from "@angular/core";
import { GuestSessionRequest, GuestSession } from "../models/session.model";
import { BrowserStorageService } from "./browser-storage.service";

export interface PersistedMessage {
  id: string;
  text: string;
  time: string;
  mine: boolean;
  replyTo?: { id: string; text: string };
}

export interface PersistedChatState {
  version: 1;
  session: GuestSession;
  profile: GuestSessionRequest;
  selectedInterests: string[];
  messages: PersistedMessage[];
  roomId: string | null;
  id: string | null;
  name: string;
  avatarUrl: string | null;
  country: string;
  sharedInterests: string[];
  isSearching: boolean;
  savedAt: number;
}

const STORAGE_KEY = "strangeryap:active-chat:v1";
const PROFILE_KEY = "strangeryap:guest-profile:v1";

@Injectable({ providedIn: "root" })
export class ChatPersistenceService {
  constructor(private readonly storage: BrowserStorageService) {}

  read(): PersistedChatState | null {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw) as PersistedChatState;
      if (value.version !== 1) {
        this.clear();
        return null;
      }
      return value;
    } catch {
      this.clear();
      return null;
    }
  }

  readSession(): {
    session: GuestSession;
    profile: GuestSessionRequest;
  } | null {
    try {
      const raw = this.storage.getItem(PROFILE_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw) as {
        version: 1;
        session: GuestSession;
        profile: GuestSessionRequest;
      };
      return value.version === 1 && value.session?.userId && value.profile
        ? value
        : null;
    } catch {
      return null;
    }
  }

  writeSession(session: GuestSession, profile: GuestSessionRequest) {
    try {
      this.storage.setItem(
        PROFILE_KEY,
        JSON.stringify({ version: 1, session, profile }),
      );
    } catch {
      // QuotaExceededError — storage full, skip persistence
    }
  }

  write(state: Omit<PersistedChatState, "version" | "savedAt">) {
    try {
      this.storage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, version: 1, savedAt: Date.now() }),
      );
    } catch {
      // QuotaExceededError — storage full, skip persistence
    }
  }

  clear() {
    this.storage.removeItem(STORAGE_KEY);
  }

  clearAll() {
    this.storage.removeItem(STORAGE_KEY);
    this.storage.removeItem(PROFILE_KEY);
  }
}
